import Grid from '@material-ui/core/Grid'
import InfoItem from './InfoItem'
import EditItem from './EditItem'
import { Formik, Form } from 'formik'
import restClient from 'Shared/hooks/restClient'
import {
  getEditableItems,
  getInitialValues,
  getValidationSchema,
  fieldMappings
} from './infoSectionHelpers'
import phone from 'phone'
import { FIELD_CONFIGS } from './fieldConfig'

const InfoSection = ({ section }) => {
  const act = useAct()
  const [editing, setEditing] = useState(false)
  const [saveError, setSaveError] = useState(null)
  const [submitForm, setSubmitForm] = useState(null)
  const mdWidth = section.columns.length === 1 ? 12 : 6
  const targetData = useSel((s) => s.target) || {}
  const { id, externalId } = targetData

  const editableItems = getEditableItems(section)
  const hasEditableItems = editableItems.length > 0

  const handleStartEdit = () => {
    setEditing(true)
    setSaveError(null)
  }

  const handleSave = (values, { setSubmitting }) => {
    setSaveError(null)
    const updateData = { externalId }

    const phoneFields = new Set(
      editableItems.flatMap(editKey =>
        (FIELD_CONFIGS[editKey] || [])
          .filter(config => config.validation === 'phone')
          .map(config => config.field)
      )
    )

    Object.keys(values).forEach(field => {
      const newValue = values[field]
      const mappedFieldName = fieldMappings[field] || field

      if (field === 'postcodeData') {
        if (newValue.postcode && newValue.postcode.trim()) {
          updateData.postcode = newValue.postcode.trim()
          updateData.country = newValue.country || 'GB'
        } else {
          updateData.postcode = ''
          updateData.country = ''
        }
        return
      }

      if (phoneFields.has(field) && newValue) {
        const country = window.ENV?.COUNTRY || 'GB'
        const formatted = phone(newValue, { country })
        updateData[mappedFieldName] = formatted.isValid ? formatted.phoneNumber : newValue
        return
      }

      updateData[mappedFieldName] = newValue
    })

    restClient.post('/api/targets/upsert', { record: updateData }).then(() => {
      setEditing(false)
      act.target.load(id)
    }).catch((error) => {
      setSaveError('Failed to save changes. Please try again.')
    }).finally(() => {
      setSubmitting(false)
    })
  }

  const handleCancel = () => {
    setEditing(false)
    setSaveError(null)
  }

  return (
    <Grid item container direction="column">
      <Grid item container direction="row" alignItems="center" justifyContent="space-between">
        <Grid item>
          <p className="larger bold no-margin-top">{section.title}</p>
        </Grid>
        <Grid item>
          {editing ? (
            <div className="flex gap-small gap-10 margin-bottom">
              <button
                className="button primary small"
                onClick={() => submitForm && submitForm()}
              >
                Save
              </button>
              <button
                className="button secondary small"
                onClick={handleCancel}
              >
                Cancel
              </button>
            </div>
          ) : (
            hasEditableItems && (
              <button
                type="button"
                className="button secondary small margin-bottom"
                onClick={handleStartEdit}
              >
                <SvgIconsEdit color="black" size={16} />
                Edit
              </button>
            )
          )}
        </Grid>
      </Grid>

      {editing && saveError && (
        <Grid item className="margin-bottom">
          <span className="error">
            {saveError}
          </span>
        </Grid>
      )}

      <Grid item container>
        {section.columns.map((column, idx) => (
          <Grid
            {...{ xs: 12, md: mdWidth, spacing: 2, direction: 'column', item: true }}
            container
            key={`${section.title}-${idx}`}
          >
            {column.map((row, i) => (
              <InfoItem
                key={i}
                type={row.type}
                identifier={row.label}
                content={row.value}
                editKey={row.editKey}
                editing={editing}
              />
            ))}
          </Grid>
        ))}
      </Grid>

      {editing && editableItems.length > 0 && (
        <Grid item container>
          <Formik
            initialValues={getInitialValues(editableItems, targetData)}
            validationSchema={getValidationSchema(editableItems)}
            onSubmit={(values, actions) => handleSave(values, actions)}
            enableReinitialize={true}
          >
            {({ submitForm: formSubmitForm, isSubmitting }) => {
              if (submitForm !== formSubmitForm) {
                setSubmitForm(() => formSubmitForm)
              }
              
              return (
                <Form style={{ width: '100%' }}>
                  {editableItems.map(editKey => (
                    <div key={editKey} className="margin-bottom">
                      <EditItem editKey={editKey} />
                    </div>
                  ))}
                </Form>
              )
            }}
          </Formik>
        </Grid>
      )}
    </Grid>
  )
}

export default InfoSection
